import { useEffect, useState } from "react";
import { Save, Loader2, Mail, Send, Search, Server, Star, RefreshCw, Truck, PanelTop, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const input = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";
const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
    className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-brand" : "bg-stone/30"}`}>
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fetchingHours, setFetchingHours] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS({ ...r.data, smtp_password: "" })); }, []);
  if (!s) return <p className="text-stone">Chargement…</p>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      if (!payload.smtp_password) delete payload.smtp_password;
      const { data } = await api.put("/settings", payload);
      setS({ ...data, smtp_password: "" });
      toast.success("Paramètres enregistrés");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const testEmail = async () => {
    setTesting(true);
    try { await api.post("/settings/test-email"); toast.success("Email de test envoyé !"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi"); }
    finally { setTesting(false); }
  };

  const syncReviews = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/reviews/sync"); toast.success(`${data.count} avis synchronisés (${data.rating}★)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de la synchronisation"); }
    finally { setSyncing(false); }
  };

  const fetchHours = async () => {
    setFetchingHours(true);
    try { const { data } = await api.post("/settings/fetch-google-hours"); set("topbar_hours", data.topbar_hours); toast.success("Horaires récupérés depuis Google"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de la récupération"); }
    finally { setFetchingHours(false); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-4xl text-forest mb-1">Paramètres</h1>
      <p className="text-stone mb-8">Notifications email et automatisation SEO</p>

      {/* Email notifications */}
      <div className="rounded-xl bg-white border border-border p-6 mb-6" data-testid="settings-email">
        <div className="flex items-center gap-2 mb-5"><Mail className="text-brand" size={20} /><h3 className="font-semibold text-forest">Notifications des nouveaux leads</h3></div>

        <div className="flex items-center justify-between mb-5 pb-5 border-b border-border">
          <div><p className="text-sm font-medium text-forest">Activer les notifications</p><p className="text-xs text-stone">Recevoir un email à chaque nouveau lead</p></div>
          <Toggle checked={s.notifications_enabled} onChange={(v) => set("notifications_enabled", v)} testid="toggle-notifications" />
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-stone">Email destinataire des alertes</label>
            <input className={input} type="email" placeholder="contact@acanthejardins.fr" value={s.notify_email} onChange={(e) => set("notify_email", e.target.value)} data-testid="notify-email" />
          </div>

          <div className="flex items-center gap-2 pt-2"><Server size={16} className="text-stone" /><p className="text-sm font-medium text-forest">Serveur SMTP</p></div>
          <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
            <div><label className="text-xs text-stone">Hôte SMTP</label><input className={input} placeholder="smtp.example.com" value={s.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} data-testid="smtp-host" /></div>
            <div><label className="text-xs text-stone">Port</label><input className={input} type="number" value={s.smtp_port} onChange={(e) => set("smtp_port", Number(e.target.value))} data-testid="smtp-port" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-stone">Utilisateur</label><input className={input} placeholder="utilisateur@example.com" value={s.smtp_user} onChange={(e) => set("smtp_user", e.target.value)} data-testid="smtp-user" /></div>
            <div><label className="text-xs text-stone">Mot de passe {s.smtp_password_set && <span className="text-green-600">(défini)</span>}</label><input className={input} type="password" placeholder={s.smtp_password_set ? "•••••••• (inchangé)" : "Mot de passe"} value={s.smtp_password} onChange={(e) => set("smtp_password", e.target.value)} data-testid="smtp-password" /></div>
          </div>
          <div className="grid sm:grid-cols-[2fr_1fr] gap-3 items-end">
            <div><label className="text-xs text-stone">Adresse expéditeur (From)</label><input className={input} placeholder="no-reply@acanthejardins.fr" value={s.smtp_from} onChange={(e) => set("smtp_from", e.target.value)} data-testid="smtp-from" /></div>
            <div className="flex items-center gap-3 pb-2"><span className="text-sm text-stone shrink-0">STARTTLS</span><Toggle checked={s.smtp_use_tls} onChange={(v) => set("smtp_use_tls", v)} testid="toggle-tls" /></div>
          </div>
          <button onClick={testEmail} disabled={testing} data-testid="test-email-btn"
            className="rounded-full border border-brand text-brand text-sm font-semibold px-5 py-2.5 hover:bg-brand/5 flex items-center gap-2 disabled:opacity-60">
            {testing ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Envoyer un email de test
          </button>
        </div>
      </div>

      {/* SEO automation */}
      <div className="rounded-xl bg-white border border-border p-6 mb-6" data-testid="settings-serpapi">
        <div className="flex items-center gap-2 mb-5"><Search className="text-brand" size={20} /><h3 className="font-semibold text-forest">Suivi automatique des positions (SerpAPI)</h3></div>
        <div className="flex items-center justify-between mb-5">
          <div><p className="text-sm font-medium text-forest">Vérification automatique</p><p className="text-xs text-stone">Met à jour les positions Google de tous vos mots-clés</p></div>
          <Toggle checked={s.serpapi_auto_enabled} onChange={(v) => set("serpapi_auto_enabled", v)} testid="toggle-serpapi" />
        </div>
        <div className="max-w-xs">
          <label className="text-xs text-stone">Fréquence</label>
          <select className={input} value={s.serpapi_frequency} onChange={(e) => set("serpapi_frequency", e.target.value)} data-testid="serpapi-frequency">
            <option value="weekly">1× par semaine (recommandé)</option>
            <option value="daily">1× par jour</option>
          </select>
          <p className="text-xs text-stone mt-1">⚠️ Chaque vérification consomme 1 crédit SerpAPI par mot-clé.</p>
        </div>
      </div>

      {/* Google reviews */}
      <div className="rounded-xl bg-white border border-border p-6 mb-6" data-testid="settings-reviews">
        <div className="flex items-center gap-2 mb-5"><Star className="text-brand" size={20} /><h3 className="font-semibold text-forest">Avis Google</h3></div>
        <div className="flex items-center justify-between mb-5">
          <div><p className="text-sm font-medium text-forest">Synchronisation automatique (hebdomadaire)</p><p className="text-xs text-stone">Récupère et affiche vos avis Google sur le site</p></div>
          <Toggle checked={s.google_reviews_enabled} onChange={(v) => set("google_reviews_enabled", v)} testid="toggle-reviews" />
        </div>
        <div>
          <label className="text-xs text-stone">Identifiant Google Maps (data_id)</label>
          <input className={input} placeholder="0x...:0x..." value={s.google_reviews_data_id || ""} onChange={(e) => set("google_reviews_data_id", e.target.value)} data-testid="reviews-dataid" />
          <p className="text-xs text-stone mt-1">Pré-rempli pour Acanthe Jardins (Saint-Victoret).</p>
        </div>
        <button onClick={syncReviews} disabled={syncing} data-testid="reviews-sync-btn"
          className="mt-4 rounded-full border border-brand text-brand text-sm font-semibold px-5 py-2.5 hover:bg-brand/5 flex items-center gap-2 disabled:opacity-60">
          {syncing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />} Synchroniser maintenant
        </button>
      </div>

      {/* Shop config */}
      <div className="rounded-xl bg-white border border-border p-6 mb-6" data-testid="settings-shop">
        <div className="flex items-center gap-2 mb-5"><Truck className="text-brand" size={20} /><h3 className="font-semibold text-forest">Boutique — livraison</h3></div>
        <div className="flex items-center justify-between mb-5">
          <div><p className="text-sm font-medium text-forest">Proposer la livraison</p><p className="text-xs text-stone">Sinon, retrait sur place uniquement</p></div>
          <Toggle checked={s.shop_delivery_enabled} onChange={(v) => set("shop_delivery_enabled", v)} testid="toggle-delivery" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-xs text-stone">Prix par km (€)</label><input type="number" step="0.1" className={input} value={s.shop_price_per_km} onChange={(e) => set("shop_price_per_km", Number(e.target.value))} data-testid="shop-ppk" /></div>
          <div><label className="text-xs text-stone">Livraison offerte à partir de (€)</label><input type="number" className={input} value={s.shop_free_delivery_threshold} onChange={(e) => set("shop_free_delivery_threshold", Number(e.target.value))} data-testid="shop-threshold" /></div>
        </div>
        <p className="text-xs text-stone mt-2">Les frais sont calculés automatiquement selon la distance depuis Saint-Victoret. La date de livraison est fixée par le service client.</p>
      </div>

      {/* Visibilité de la boutique */}
      <div className="rounded-xl bg-white border border-border p-6 mb-6" data-testid="settings-shop">
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium text-forest">Boutique en ligne visible</p><p className="text-xs text-stone">Affiche l'onglet Boutique, le panier et les pages produits sur le site public. Désactivée = boutique masquée (le back-office reste accessible).</p></div>
          <Toggle checked={s.shop_enabled} onChange={(v) => set("shop_enabled", v)} testid="toggle-shop" />
        </div>
      </div>

      {/* Top bar (barre supérieure PC) */}
      <div className="rounded-xl bg-white border border-border p-6 mb-6" data-testid="settings-topbar">
        <div className="flex items-center gap-2 mb-5"><PanelTop className="text-brand" size={20} /><h3 className="font-semibold text-forest">Barre supérieure (PC)</h3></div>
        <div className="flex items-center justify-between mb-5 pb-5 border-b border-border">
          <div><p className="text-sm font-medium text-forest">Afficher la barre du haut</p><p className="text-xs text-stone">Coordonnées, horaires et bouton devis (ordinateur uniquement)</p></div>
          <Toggle checked={s.topbar_enabled} onChange={(v) => set("topbar_enabled", v)} testid="toggle-topbar" />
        </div>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-stone">Téléphone principal</label><input className={input} value={s.topbar_phone || ""} onChange={(e) => set("topbar_phone", e.target.value)} data-testid="topbar-phone" /></div>
            <div><label className="text-xs text-stone">Téléphone secondaire</label><input className={input} value={s.topbar_phone2 || ""} onChange={(e) => set("topbar_phone2", e.target.value)} data-testid="topbar-phone2" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-xs text-stone">Email</label><input className={input} value={s.topbar_email || ""} onChange={(e) => set("topbar_email", e.target.value)} data-testid="topbar-email" /></div>
            <div><label className="text-xs text-stone">Adresse / zone</label><input className={input} value={s.topbar_address || ""} onChange={(e) => set("topbar_address", e.target.value)} data-testid="topbar-address" /></div>
          </div>
          <div>
            <label className="text-xs text-stone">Horaires d'ouverture</label>
            <div className="flex gap-2">
              <input className={input} placeholder="Lun–Ven 8h–18h · Sam 9h–12h" value={s.topbar_hours || ""} onChange={(e) => set("topbar_hours", e.target.value)} data-testid="topbar-hours" />
              <button onClick={fetchHours} disabled={fetchingHours} data-testid="fetch-hours-btn"
                className="shrink-0 rounded-lg border border-brand text-brand text-sm font-semibold px-4 hover:bg-brand/5 flex items-center gap-2 disabled:opacity-60">
                {fetchingHours ? <Loader2 className="animate-spin" size={16} /> : <Clock size={16} />} Depuis Google
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1"><span className="text-sm text-stone">Afficher le bouton « Devis gratuit »</span><Toggle checked={s.topbar_show_devis} onChange={(v) => set("topbar_show_devis", v)} testid="toggle-topbar-devis" /></div>

          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <div><p className="text-sm font-medium text-forest">Message personnalisé</p><p className="text-xs text-stone">Ex : « Fermé pour congés du 1er au 15 août », « Joyeuses fêtes 🎄 »</p></div>
              <Toggle checked={s.topbar_message_enabled} onChange={(v) => set("topbar_message_enabled", v)} testid="toggle-topbar-message" />
            </div>
            <input className={input} placeholder="Votre message d'annonce" value={s.topbar_message || ""} onChange={(e) => set("topbar_message", e.target.value)} data-testid="topbar-message" />
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="settings-save-btn"
        className="rounded-full bg-brand text-cream font-semibold px-8 py-3 hover:bg-forest transition-colors flex items-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Enregistrer les paramètres
      </button>
    </div>
  );
}